/**
 * Baghdad Governorate Service Area Geofence
 *
 * Covers the FULL Baghdad Governorate administrative boundary including:
 * - North: Taji, Tarmiyah
 * - East: Mada'in, Jisr Diyala, Rustamiyah, Nahrawan
 * - South: Mahmudiyah, Yusufiyah, Latifiyah, Rasheed
 * - West: Abu Ghraib, Baghdad Airport, Radwaniyah
 * - Central: All Baghdad city districts (Karkh, Rusafa, Sadr City, Kadhimiya, etc.)
 */

// Baghdad Governorate bounding box for Mapbox maxBounds [SW corner, NE corner]
// Covers the entire governorate with padding
export const BAGHDAD_BOUNDS: [[number, number], [number, number]] = [
  [43.85, 32.95], // Southwest: lng, lat
  [44.85, 33.65], // Northeast: lng, lat
];

// Baghdad center coordinates
export const BAGHDAD_CENTER = { lat: 33.3152, lng: 44.3661 };

// Baghdad Governorate administrative boundary polygon (lat, lng pairs)
// Generous boundary covering the entire governorate
export const BAGHDAD_POLYGON: [number, number][] = [
  // North — Tarmiyah / Taji
  [33.58, 44.1], // NW corner — Tarmiyah west
  [33.6, 44.2], // N — Tarmiyah
  [33.62, 44.3], // N — north of Taji
  [33.6, 44.42], // NE — Taji east
  [33.58, 44.55], // NE corner — towards Khalis border

  // East — Nahrawan / Mada'in
  [33.52, 44.62], // E — Nahrawan north
  [33.45, 44.68], // E — Nahrawan
  [33.38, 44.72], // E — east of Mada'in
  [33.3, 44.72], // E — Mada'in / Salman Pak approach
  [33.22, 44.68], // SE — Jisr Diyala south
  [33.15, 44.62], // SE corner — Salman Pak

  // South — Mahmudiyah / Yusufiyah / Latifiyah
  [33.08, 44.55], // S — Mahmudiyah east
  [33.05, 44.45], // S — Mahmudiyah
  [33.03, 44.35], // S — Yusufiyah / Latifiyah
  [33.05, 44.25], // S — Latifiyah west
  [33.08, 44.15], // SW — Yusufiyah west

  // West — Abu Ghraib / Radwaniyah
  [33.15, 44.05], // SW corner — Abu Ghraib south
  [33.22, 43.98], // W — Abu Ghraib west
  [33.3, 43.95], // W — Radwaniyah
  [33.38, 43.95], // W — west Baghdad
  [33.45, 43.98], // NW — towards Tarmiyah
  [33.52, 44.02], // NW — Tarmiyah south
  [33.58, 44.1], // Close polygon — back to NW corner
];

// GeoJSON for Mapbox overlay — the "mask" that darkens everything outside Baghdad
export const BAGHDAD_MASK_GEOJSON: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        // Outer ring = world bounds, Inner ring = Baghdad cutout (reversed winding)
        coordinates: [
          // Outer ring (world) — clockwise
          [
            [-180, -90],
            [180, -90],
            [180, 90],
            [-180, 90],
            [-180, -90],
          ],
          // Inner ring (Baghdad cutout) — counter-clockwise to create hole
          BAGHDAD_POLYGON.map(([lat, lng]) => [lng, lat]).reverse(),
        ],
      },
    },
  ],
};

// Baghdad boundary as GeoJSON line for the glowing border
export const BAGHDAD_BORDER_GEOJSON: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: BAGHDAD_POLYGON.map(([lat, lng]) => [lng, lat]),
      },
    },
  ],
};

/**
 * Ray-casting point-in-polygon algorithm.
 * Returns true if (lat, lng) is inside the Baghdad Governorate boundary.
 */
export const isInsideBaghdad = (lat: number, lng: number): boolean => {
  const polygon = BAGHDAD_POLYGON;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [yi, xi] = polygon[i]; // lat, lng
    const [yj, xj] = polygon[j];

    const intersect = yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;

    if (intersect) inside = !inside;
  }

  return inside;
};

/* ── Province Operations: the map beyond Baghdad ─────────────────────────────
 *
 * Baghdad keeps its full geofence (bounds, mask, glowing border, polygon
 * validation) exactly as before. Any OTHER selected province unlocks the map
 * to Iraq-wide bounds, centers on that province's capital, and validates the
 * pin against Iraq's bounding box — the ADDRESS form is not the place to
 * encode Boxy's service area, order creation is (422 PROVINCE_NOT_LIVE).
 */

// Iraq bounding box for Mapbox maxBounds [SW corner, NE corner] (lng, lat)
export const IRAQ_BOUNDS: [[number, number], [number, number]] = [
  [38.5, 28.8], // Southwest
  [49.0, 37.6], // Northeast
];

export const IRAQ_CENTER = { lat: 33.2, lng: 43.7 };

// Governorate capitals, keyed by cities.province_code. A code missing here
// simply falls back to the Iraq-wide view — never a crash, never a lock.
export const PROVINCE_CENTERS: Record<string, { lat: number; lng: number }> = {
  BGD: BAGHDAD_CENTER,
  BAS: { lat: 30.5085, lng: 47.7804 }, // Basra
  KRB: { lat: 32.616, lng: 44.0249 }, // Karbala
  NJF: { lat: 31.9964, lng: 44.314 }, // Najaf
  BBL: { lat: 32.4637, lng: 44.4199 }, // Babil (Hilla)
  ARB: { lat: 36.1911, lng: 44.0092 }, // Erbil
  MOS: { lat: 36.335, lng: 43.1189 }, // Nineveh (Mosul)
  DWN: { lat: 31.989, lng: 44.9247 }, // Al-Qadisiyyah (Diwaniyah)
};

export const isInsideIraq = (lat: number, lng: number): boolean =>
  lng >= IRAQ_BOUNDS[0][0] &&
  lng <= IRAQ_BOUNDS[1][0] &&
  lat >= IRAQ_BOUNDS[0][1] &&
  lat <= IRAQ_BOUNDS[1][1];

export interface ProvinceGeofence {
  /** true = the full Baghdad treatment: mask, border, polygon validation */
  isBaghdad: boolean;
  bounds: [[number, number], [number, number]];
  center: { lat: number; lng: number };
  zoom: number;
  isInside: (lat: number, lng: number) => boolean;
}

/**
 * The map behaviour for a selected province. No code (legacy callers, nothing
 * selected yet) keeps today's Baghdad behaviour unchanged.
 */
export const geofenceForProvince = (provinceCode?: string | null): ProvinceGeofence => {
  if (!provinceCode || provinceCode === 'BGD') {
    return {
      isBaghdad: true,
      bounds: BAGHDAD_BOUNDS,
      center: BAGHDAD_CENTER,
      zoom: 11,
      isInside: isInsideBaghdad,
    };
  }

  const center = PROVINCE_CENTERS[provinceCode];

  return {
    isBaghdad: false,
    bounds: IRAQ_BOUNDS,
    center: center ?? IRAQ_CENTER,
    zoom: center ? 11 : 5,
    isInside: isInsideIraq,
  };
};
